// Sovereign GitHub Repository Live Telemetry Client
// Automatically queries public repository metadata with persistent client caching

const CACHE_KEY = 'memark_gh_telemetry_cache';
const CACHE_TTL_MS = 6 * 60 * 60 * 1000; // 6 hours

function getCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function setCache(cache) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch (e) {
    console.warn('Failed to save GitHub cache', e);
  }
}

export function parseGitHubRepo(url) {
  if (!url || !url.includes('github.com')) return null;
  try {
    const parsed = new URL(url);
    if (!parsed.hostname.includes('github.com')) return null;
    const parts = parsed.pathname.split('/').filter(Boolean);
    if (parts.length >= 2) {
      const owner = parts[0];
      const repo = parts[1].replace(/\.git$/, '');
      if (['orgs', 'settings', 'trending', 'explore', 'notifications', 'marketplace'].includes(owner)) {
        return null;
      }
      return { owner, repo, fullName: `${owner}/${repo}` };
    }
  } catch {
    return null;
  }
  return null;
}

export function formatNumber(num) {
  if (!num && num !== 0) return '0';
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'k';
  return String(num);
}

export async function fetchGitHubTelemetry(url) {
  const repoInfo = parseGitHubRepo(url);
  if (!repoInfo) return null;

  const cache = getCache();
  const cached = cache[repoInfo.fullName];
  const now = Date.now();

  if (cached && (now - cached.timestamp < CACHE_TTL_MS)) {
    return cached.data;
  }

  try {
    const res = await fetch(`https://api.github.com/repos/${repoInfo.fullName}`, {
      headers: {
        'Accept': 'application/vnd.github.v3+json',
      }
    });

    if (res.status === 200) {
      const data = await res.json();
      const telemetry = {
        fullName: repoInfo.fullName,
        stars: formatNumber(data.stargazers_count),
        forks: formatNumber(data.forks_count),
        language: data.language || 'Code',
        description: data.description || '',
        license: data.license ? data.license.spdx_id || data.license.name : null,
      };

      cache[repoInfo.fullName] = { timestamp: now, data: telemetry };
      setCache(cache);
      return telemetry;
    } else if (res.status === 403 || res.status === 429) {
      // Rate limited, return fallback from cache if exists or minimal info
      return cached ? cached.data : { fullName: repoInfo.fullName, stars: null, language: 'Repo' };
    }
  } catch (e) {
    // Return stale cache if network error
    if (cached) return cached.data;
  }

  return null;
}

export function getCachedGitHubTelemetrySync(url) {
  const repoInfo = parseGitHubRepo(url);
  if (!repoInfo) return null;
  const cache = getCache();
  const cached = cache[repoInfo.fullName];
  return cached ? cached.data : null;
}
